import dayjs from "dayjs";
import styled from "styled-components";

import { SWReactIcons } from "@similarweb/icons";
import type { ColumnsType } from "@similarweb/pro-logic-components";
import { shortMonthDayAndYear } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { IconButton, Tooltip } from "@similarweb/ui-components";

import { CoreWebsiteCell } from "pro-features/components/core cells/src/CoreWebsiteCell/CoreWebsiteCell";
import { getIndexCellPropsForTableV2 } from "app/components/React/Table/cells/IndexCell";
import { translate } from "app/filters/i18nFilter";
import { numberFilter } from "app/filters/numberFilter";
import type { AdIntelligenceController } from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import { allTrackers } from "app/services/track/track";
import { CenteredFlexColumn, CenteredFlexRow } from "pro-features/styled components/StyledFlex/src/StyledFlex";

import { AdCardImage } from "./AdCardImage";
import { getCreativeTypeIcon } from "./AdIntelligenceAdCard";
import { creativeTypeToInfoMap } from "./hooks/useAdsGallery";
import { CreativeTypeIconWrapper, ImgContainer, LeftFlexInlineRow } from "./StyledComponents";
import { UrlAnchor } from "./UrlAnchor";
import { NA_VALUE } from "app/pages/digital-marketing/ad-intelligence/common/utils/utils";

const ImageCellContainer = styled(CenteredFlexColumn)`
    position: relative;
    cursor: pointer;

    ${ImgContainer} {
        padding: 4px;
        height: 154px;
    }
`;

export const ShowModalButtonWrapper = styled.div`
    position: absolute;
    right: 0;
    top: 50%;
    transform: translateY(-50%);
    opacity: 0;

    ${SWReactIcons} svg path, &:hover ${SWReactIcons} svg path {
        fill: ${colorsPalettes.carbon[0]};
    }
`;

const CenteredPlayIcon = styled(CenteredFlexRow)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    opacity: 0;
    transition: opacity 0.2s ease;
    pointer-events: none;

    ${ImageCellContainer}:hover & {
        opacity: 1;
    }
`;

type AnyObject = Record<PropertyKey, any>;

export const getColumns = (
    currentPage: number,
    pageSize: number,
    onAdClick: (index: number) => void,
    controller: AdIntelligenceController,
    withoutTooltip = false,
    showAdvertiserColumn = false,
): ColumnsType<AnyObject> =>
    [
        {
            title: "",
            align: "center",
            fixed: "left",
            key: "index",
            ...getIndexCellPropsForTableV2(currentPage, pageSize),
        },
        ...(showAdvertiserColumn
            ? [
                  {
                      dataIndex: "advertiser",
                      fixed: "left",
                      title: translate("ad.intelligence.ads.table.advertiser.title"),
                      tooltipTitle: translate("ad.intelligence.ads.table.advertiser.title"),
                      tooltipSubtitle: translate("ad.intelligence.ads.table.advertiser.title.tooltip"),
                      render: (advertiser, { advertiserFavicon }) => {
                          const props = {
                              domain: advertiser,
                              icon: advertiserFavicon,
                              hideTrackButton: true,
                              TooltipComponent: ({ children }) => <Tooltip content={advertiser}>{children}</Tooltip>,
                              textAndIconMargin: "8px",
                          };
                          return <CoreWebsiteCell {...props} />;
                      },
                      width: 152,
                      sortable: false,
                  },
              ]
            : []),
        {
            title: translate("ad.intelligence.advertiser.ads.image.title"),
            tooltipTitle: translate("ad.intelligence.advertiser.ads.image.tooltip.title"),
            tooltipSubtitle: translate("ad.intelligence.advertiser.ads.image.tooltip"),
            dataIndex: "creativeImageUri",
            width: 240,
            align: "left",
            fixed: "left",
            render: (creativeImageUri, { dimensions, creativeType }, index) => {
                const [width, height] = dimensions?.split("x") ?? [];

                return (
                    <ImageCellContainer onClick={() => onAdClick(index)}>
                        <AdCardImage
                            src={creativeImageUri}
                            width={width}
                            height={height}
                            isVideo={creativeType === "video"}
                        />
                        {creativeType === "video" ? (
                            <CenteredPlayIcon>
                                <SWReactIcons
                                    iconName="play-borderless"
                                    size="xl"
                                    iconColor={colorsPalettes.carbon[0]}
                                />
                            </CenteredPlayIcon>
                        ) : null}
                        <ShowModalButtonWrapper>
                            <Tooltip
                                placement="top"
                                content={translate("ad.intelligence.advertiser.ads.image.hover.button.tooltip")}
                            >
                                <div style={{ marginTop: 8 }}>
                                    <IconButton
                                        type="primary"
                                        iconName="resize"
                                        iconSize="xs"
                                        width={24}
                                        height={24}
                                        style={{ borderRadius: 4 }}
                                    />
                                </div>
                            </Tooltip>
                        </ShowModalButtonWrapper>
                    </ImageCellContainer>
                );
            },
        },
        ...(controller === "publisher"
            ? [
                  {
                      dataIndex: "advertiser",
                      fixed: "left",
                      title: translate("ad.intelligence.ads.table.advertiser.title"),
                      tooltipTitle: translate("ad.intelligence.ads.table.advertiser.title"),
                      tooltipSubtitle: translate("ad.intelligence.ads.table.advertiser.title.tooltip"),
                      render: (advertiser, { advertiserFavicon }) => {
                          const { country, duration, webSource } = proNavigator.getParams();
                          const props = {
                              domain: advertiser,
                              icon: advertiserFavicon,
                              internalLink: proNavigator.href("adintelligence.root.frame.advertisersOverview", {
                                  key: advertiser,
                                  country,
                                  duration,
                                  webSource,
                              }),
                              trackInternalLink: (e) => {
                                  e.stopPropagation();
                                  allTrackers.dispatchTrackEvent("Internal Link", "click", `Table/${advertiser}`);
                              },
                              externalLink: `http://${advertiser}`,
                              trackExternalLink: (e) => {
                                  e.stopPropagation();
                                  allTrackers.dispatchTrackEvent("External Link", "click", `Table/${advertiser}`);
                              },
                              hideTrackButton: true,
                              TooltipComponent: ({ children }) => (
                                  <Tooltip
                                      content={translate(
                                          "ad.intelligence.display.advertiser.publisher.ads.advertiser.column.link.tooltip",
                                          { domain: advertiser },
                                      )}
                                  >
                                      {children}
                                  </Tooltip>
                              ),
                              textAndIconMargin: "8px",
                          };
                          return <CoreWebsiteCell {...props} />;
                      },
                      width: 152,
                      sortable: false,
                  },
              ]
            : []),

        {
            title: translate("ad.intelligence.advertiser.ads.size.column.title"),
            tooltipTitle: translate("ad.intelligence.advertiser.ads.size.column.tooltip.title"),
            tooltipSubtitle: translate("ad.intelligence.advertiser.ads.size.column.tooltip"),
            dataIndex: "dimensions",
            width: 120,
            align: "left",
            render: (dimensions) => dimensions?.split("x").join(" X ") + " px",
        },
        {
            title: translate("ad.intelligence.advertiser.ads.creativeType.title"),
            tooltipTitle: translate("ad.intelligence.advertiser.ads.creativeType.tooltip.title"),
            tooltipSubtitle: translate("ad.intelligence.advertiser.ads.creativeType.tooltip"),
            dataIndex: "creativeType",
            width: 120,
            align: "left",
            render: (creativeType, { dimensions }) => (
                <LeftFlexInlineRow>
                    <CreativeTypeIconWrapper
                        data-automation-ad-type={translate(creativeTypeToInfoMap[creativeType].name)}
                    >
                        {getCreativeTypeIcon(creativeType, dimensions)}
                    </CreativeTypeIconWrapper>
                    <span>{translate(creativeTypeToInfoMap[creativeType].name)}</span>
                </LeftFlexInlineRow>
            ),
        },
        {
            title: translate("ad.intelligence.advertiser.ads.landingPage.title"),
            tooltipTitle: translate("ad.intelligence.advertiser.ads.landingPage.tooltip.title"),
            tooltipSubtitle: translate("ad.intelligence.advertiser.ads.landingPage.tooltip"),
            dataIndex: "landingPagesLinks",
            width: 170,
            align: "left",
            render: (landingPagesLinks) =>
                landingPagesLinks && landingPagesLinks.length > 0 ? (
                    <UrlAnchor landingPagesLinks={landingPagesLinks} />
                ) : (
                    NA_VALUE
                ),
        },
        {
            title: translate("ad.intelligence.advertiser.ads.activeDays.title"),
            tooltipTitle: translate("ad.intelligence.advertiser.ads.activeDays.tooltip.title"),
            tooltipSubtitle: translate("ad.intelligence.advertiser.ads.activeDays.tooltip"),
            dataIndex: "activeDays",
            width: 120,
            align: "left",
            render: (activeDays) => {
                return `${numberFilter()(activeDays)} ${translate(
                    activeDays > 1
                        ? "ad.intelligence.advertiser.ad.modal.details.duration.days"
                        : "ad.intelligence.advertiser.ad.modal.details.duration.day",
                )}`;
            },
        },
        {
            title: translate("ad.intelligence.advertiser.ads.first.seen.column.title"),
            tooltipTitle: translate("ad.intelligence.advertiser.ads.first.seen.column.tooltip.title"),
            tooltipSubtitle: translate("ad.intelligence.advertiser.ads.first.seen.column.tooltip"),
            dataIndex: "firstSeen",
            width: 118,
            align: "left",
            render: (firstSeen) => dayjs(firstSeen).format(shortMonthDayAndYear),
        },
        {
            title: translate("ad.intelligence.advertiser.ads.last.seen.column.title"),
            tooltipTitle: translate("ad.intelligence.advertiser.ads.last.seen.column.tooltip.title"),
            tooltipSubtitle: translate("ad.intelligence.advertiser.ads.last.seen.column.tooltip"),
            dataIndex: "lastSeen",
            width: 118,
            align: "left",
            render: (lastSeen) => dayjs(lastSeen).format(shortMonthDayAndYear),
        },
    ].map((column) =>
        withoutTooltip ? { ...column, tooltipTitle: undefined, tooltipSubtitle: undefined } : column,
    ) as ColumnsType<AnyObject>;
